import React, { useState, useEffect, useRef } from "react";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { api, getApiErrorMessage } from "@/lib/api";
import {
  TANK_SENSOR_TYPE_OPTIONS,
  getTankSensorTypeCode,
  getTankSensorTypeLabel,
  normalizeTankSensorType,
} from "@/lib/tankSensorTypes";
import {
  DEFAULT_GSM_APN_CODE,
  GSM_APN_OPTIONS,
  LEVEL_CONNECTION_TYPE_OPTIONS,
  getGsmApnCode,
  getGsmApnLabel,
  getLevelConnectionTypeCode,
  getLevelConnectionTypeLabel,
} from "@/lib/networkConnectionTypes";
import {
  Building2,
  Droplets,
  Zap,
  Network,
  Activity,
  Waves,
  Edit2,
  Trash2,
  X,
  Loader,
  RefreshCw,
  Search,
} from "lucide-react";
import mqtt from "mqtt";

const MQTT_BROKER_URL =
  process.env.REACT_APP_MQTT_BROKER_URL || "wss://aquatank.aquasense.tech:9001";
const MQTT_USERNAME = process.env.REACT_APP_MQTT_USERNAME || "aqua-tank";
const MQTT_PASSWORD = process.env.REACT_APP_MQTT_PASSWORD || "fIExd783QL9z";
const MQTT_D2C_TOPIC = process.env.REACT_APP_MQTT_D2C_TOPIC || "AQUATANK/D2C/+";
const MQTT_C2D_PREFIX = process.env.REACT_APP_MQTT_C2D_PREFIX || "AQUATANK/C2D";
const CONFIG_ACK_TOAST_ID = "components-device-config-ack";
const SOURCE_STATUS_OPTIONS = [
  { value: "0", label: "Not Connected" },
  { value: "1", label: "Connected" },
];
const SOURCE_SENSOR_TYPE_OPTIONS = [
  { value: "1", label: "Hydrostatic (BS8PT)" },
  { value: "2", label: "Hydrostatic (BS8L1)" },
  { value: "3", label: "TL_Float" },
  { value: "4", label: "TL_Online" },
];
const MODBUS_DATA_TYPE_OPTIONS = [
  { value: "1", label: "U16" },
  { value: "2", label: "S16" },
  { value: "3", label: "U32" },
  { value: "4", label: "S32" },
  { value: "5", label: "FLOAT32" },
  { value: "6", label: "SPLIT_INT_DEC" },
];
const MODBUS_ORDER_OPTIONS = [
  { value: "0", label: "NA" },
  { value: "1", label: "ABCD" },
  { value: "2", label: "BADC" },
  { value: "3", label: "CDAB" },
  { value: "4", label: "DCBA" },
];
const CONNECTED_STATUS_OPTIONS = [
  { value: "1", label: "Connected" },
  { value: "0", label: "Not Connected" },
];
const ENERGY_METER_MAKE_OPTIONS = [{ value: "1", label: "SELECT_EM4M3PC100A" }];
const PUMP_CONTROL_TYPE_OPTIONS = [
  { value: "0", label: "Direct" },
  { value: "1", label: "VFD" },
  { value: "2", label: "PressureRange" },
  { value: "3", label: "VFD_Direct" },
];
const getOptionLabel = (options, value) =>
  options.find((option) => option.value === String(value))?.label ?? "-";
const formatDisplayValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;
const formatDisplayUnit = (value, unit) =>
  value === null || value === undefined || value === ""
    ? "-"
    : `${value} ${unit}`;
const normalizeEnergyMeterMake = (value) => {
  const text = String(value ?? "").trim();
  const option = ENERGY_METER_MAKE_OPTIONS.find(
    (item) => item.value === text || item.label === text,
  );
  return option?.value || ENERGY_METER_MAKE_OPTIONS[0].value;
};

const isVfdPumpControlType = (controlType) => [1, 3].includes(Number(controlType));

const normalizeConfigResult = (value) => {
  const result = String(value || "")
    .trim()
    .toLowerCase();
  return result === "failed" ? "fail" : result;
};

const filterData = (data, searchTerm, searchFields) => {
  if (!searchTerm) return data;
  return data.filter((item) =>
    searchFields.some((field) =>
      String(item[field] || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()),
    ),
  );
};

const getAssetId = (item, assetType) => {
  const idMap = {
    tower: "id",
    source: "id",
    flowmeter: "id",
    ug_tank: "id",
    atbc: "device_id",
    atbpc: "device_id",
    aquatank: "device_id",
    additional_sensor: "id",
    pump: "id",
    tank: "id",
    valve: "id",
  };
  return item[idMap[assetType]] || item.id;
};

const assetTypeMap = {
  towers: "tower",
  sources: "source",
  flowmeter: "flowmeter",
  "ug-tanks": "ug_tank",
  "pump-controllers": "atbc",
  "borewell-pump-ctrl": "atbpc",
  "level-controllers": "aquatank",
  "additional-sensors": "additional_sensor",
  pumps: "pump",
  tanks: "tank",
  valves: "valve",
};

const pumpVfdColumnKeys = new Set([
  "vfdSlaveId",
  "vfdPressureSlaveId",
  "pressureStartAddress",
  "pressureQuantity",
  "pressureDataType",
  "pressureOrder",
  "pressureScale",
  "pressureOffset",
  "rangeLowBar",
  "rangeHighBar",
  "minFreqHz",
  "maxFreqHz",
]);

const pumpPressureRangeColumnKeys = new Set([
  "pressureRangeIsSource",
  "pressureRangePtId",
  "pressureRangeStageIndex",
  "pressureRangePtSlaveId",
  "pressureRangePtStartAddress",
  "pressureRangePtQuantity",
  "pressureRangePtDataType",
  "pressureRangePtOrder",
  "pressureRangePtScale",
  "pressureRangePtOffset",
  "pressureRangeLowBar",
  "pressureRangeHighBar",
]);

const DeleteButton = ({ assetType, id, onDelete }) => (
  <Button
    variant="ghost"
    size="sm"
    className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
    onClick={() => onDelete(assetType, id)}
    title={`Delete this ${assetType}`}
  >
    <Trash2 className="h-4 w-4" />
  </Button>
);

const EditButton = ({ assetType, item, onEdit }) => (
  <Button
    variant="ghost"
    size="sm"
    className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
    onClick={() => onEdit(assetType, item)}
    title={`Edit this ${assetType}`}
  >
    <Edit2 className="h-4 w-4" />
  </Button>
);

const AssetTable = ({
  data,
  columns,
  emptyMessage,
  assetType,
  hasActions = false,
  onEdit,
  onDelete,
}) => (
  <div className="rounded-lg border border-slate-200 bg-white">
    <div className="overflow-x-auto pb-2 [scrollbar-gutter:stable]">
      <Table className="min-w-max">
        <TableHeader className="bg-slate-50">
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className="whitespace-nowrap px-5 py-4 align-middle font-semibold text-slate-700"
              >
                {col.label}
              </TableHead>
            ))}
            {hasActions && (
              <TableHead className="w-28 whitespace-nowrap px-5 py-4 text-center align-middle font-semibold text-slate-700">
                Actions
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length > 0 ? (
            data.map((item, idx) => (
              <TableRow
                key={item.id || idx}
                className="hover:bg-slate-50 transition-colors"
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className="whitespace-nowrap px-5 py-4 align-middle"
                  >
                    <span className="text-slate-700">
                      {col.render
                        ? col.render(item[col.key], item)
                        : formatDisplayValue(item[col.key])}
                    </span>
                  </TableCell>
                ))}
                {hasActions && (
                  <TableCell className="whitespace-nowrap px-5 py-4 text-center align-middle">
                    <div className="flex items-center justify-center gap-2">
                      <EditButton
                        assetType={assetType}
                        item={item}
                        onEdit={onEdit}
                      />
                      <DeleteButton
                        assetType={assetType}
                        id={getAssetId(item, assetType)}
                        onDelete={onDelete}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + (hasActions ? 1 : 0)}
                className="text-center py-8 text-slate-500"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  </div>
);

const AssetSection = ({
  title,
  icon: Icon,
  data,
  searchTerm,
  setSearchTerm,
  columns,
  bgColor,
  emptyMessage,
  onEdit,
  onDelete,
}) => {
  const assetType =
    assetTypeMap[title.toLowerCase().replace(/\s+/g, "-")] || "unknown";
  const filteredData = filterData(
    data,
    searchTerm,
    columns.map((c) => c.key),
  );
  const visibleColumns =
    assetType === "pump"
      ? columns.filter((column) => {
          const hasVfdRows = filteredData.some(
            (item) => Number(item.control_type) === 1,
          );
          const hasPressureRangeRows = filteredData.some(
            (item) => Number(item.control_type) === 2,
          );

          if (pumpVfdColumnKeys.has(column.key)) return hasVfdRows;
          if (pumpPressureRangeColumnKeys.has(column.key)) {
            return hasPressureRangeRows;
          }

          return true;
        })
      : columns;
  const pumpTailColumnKeys = new Set(["control", "created_at"]);

  if (assetType === "pump") {
    return (
      <PumpAssetSection
        data={data}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        baseColumns={columns.filter(
          (column) =>
            !pumpVfdColumnKeys.has(column.key) &&
            !pumpPressureRangeColumnKeys.has(column.key) &&
            !pumpTailColumnKeys.has(column.key),
        )}
        vfdColumns={columns.filter((column) =>
          pumpVfdColumnKeys.has(column.key),
        )}
        pressureRangeColumns={columns.filter((column) =>
          pumpPressureRangeColumnKeys.has(column.key),
        )}
        tailColumns={columns.filter((column) =>
          pumpTailColumnKeys.has(column.key),
        )}
        emptyMessage={emptyMessage}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-lg text-white ${bgColor}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">
            Total: <span className="font-semibold">{data.length}</span>
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={`Search ${title.toLowerCase()}...`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <AssetTable
        data={filteredData}
        columns={visibleColumns}
        emptyMessage={emptyMessage}
        assetType={assetType}
        hasActions={true}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
};

const PumpAssetSection = ({
  data,
  searchTerm,
  setSearchTerm,
  baseColumns,
  vfdColumns,
  pressureRangeColumns,
  tailColumns,
  emptyMessage,
  onEdit,
  onDelete,
}) => {
  const allColumns = [
    ...baseColumns,
    ...vfdColumns,
    ...pressureRangeColumns,
    ...tailColumns,
  ];
  const filteredData = filterData(
    data,
    searchTerm,
    allColumns.map((c) => c.key),
  );
  const vfdPumps = filteredData.filter(
    (item) => Number(item.control_type) === 1,
  );
  const pressureRangePumps = filteredData.filter(
    (item) => Number(item.control_type) === 2,
  );
  const vfdDirectPumps = filteredData.filter(
    (item) => Number(item.control_type) === 3,
  );
  const basicPumps = filteredData.filter(
    (item) => Number(item.control_type) === 0,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 p-2.5 text-white">
          <Activity className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Pumps</h2>
          <p className="text-sm text-slate-500">
            Total: <span className="font-semibold">{data.length}</span>
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search pumps..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {filteredData.length === 0 ? (
        <AssetTable
          data={[]}
          columns={[...baseColumns, ...tailColumns]}
          emptyMessage={emptyMessage}
          assetType="pump"
          hasActions={true}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ) : (
        <div className="space-y-6">
          {vfdPumps.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700">
                VFD Pumps
              </h3>
              <AssetTable
                data={vfdPumps}
                columns={[...baseColumns, ...vfdColumns, ...tailColumns]}
                emptyMessage="No VFD pumps found"
                assetType="pump"
                hasActions={true}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          )}

          {pressureRangePumps.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700">
                Pressure Range Pumps
              </h3>
              <AssetTable
                data={pressureRangePumps}
                columns={[
                  ...baseColumns,
                  ...pressureRangeColumns,
                  ...tailColumns,
                ]}
                emptyMessage="No pressure range pumps found"
                assetType="pump"
                hasActions={true}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          )}

          {basicPumps.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700">
                Basic Sensor Pumps
              </h3>
              <AssetTable
                data={basicPumps}
                columns={[...baseColumns, ...tailColumns]}
                emptyMessage="No basic sensor pumps found"
                assetType="pump"
                hasActions={true}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          )}

          {vfdDirectPumps.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-slate-700">
                VFD Direct Pumps
              </h3>
              <AssetTable
                data={vfdDirectPumps}
                columns={[...baseColumns, ...tailColumns]}
                emptyMessage="No VFD direct pumps found"
                assetType="pump"
                hasActions={true}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FlowmeterAssetSection = ({
  data,
  searchTerm,
  setSearchTerm,
  onEdit,
  onDelete,
}) => {
  const columns = [
    { key: "name", label: "Name" },
    {
      key: "status",
      label: "Status",
      render: (val) => getOptionLabel(CONNECTED_STATUS_OPTIONS, val),
    },
    { key: "slave_id", label: "Slave ID" },
    {
      key: "totalizerAddr",
      label: "Totalizer Address",
      render: (val, item) => item.totalizer?.addr ?? "-",
    },
    {
      key: "totalizerQuantity",
      label: "Totalizer Quantity",
      render: (val, item) => item.totalizer?.quantity ?? "-",
    },
    {
      key: "totalizerDataType",
      label: "Totalizer Data Type",
      render: (val, item) =>
        getOptionLabel(MODBUS_DATA_TYPE_OPTIONS, item.totalizer?.data_type),
    },
    {
      key: "totalizerOrder",
      label: "Totalizer Order",
      render: (val, item) =>
        getOptionLabel(MODBUS_ORDER_OPTIONS, item.totalizer?.order),
    },
    {
      key: "totalizerScale",
      label: "Totalizer Scale",
      render: (val, item) => item.totalizer?.scale ?? "-",
    },
    {
      key: "totalizerOffset",
      label: "Totalizer Offset",
      render: (val, item) => item.totalizer?.offset ?? "-",
    },
    {
      key: "flowrateAddr",
      label: "Flowrate Address",
      render: (val, item) => item.flowrate?.addr ?? "-",
    },
    {
      key: "flowrateQuantity",
      label: "Flowrate Quantity",
      render: (val, item) => item.flowrate?.quantity ?? "-",
    },
    {
      key: "flowrateDataType",
      label: "Flowrate Data Type",
      render: (val, item) =>
        getOptionLabel(MODBUS_DATA_TYPE_OPTIONS, item.flowrate?.data_type),
    },
    {
      key: "flowrateOrder",
      label: "Flowrate Order",
      render: (val, item) =>
        getOptionLabel(MODBUS_ORDER_OPTIONS, item.flowrate?.order),
    },
    {
      key: "flowrateScale",
      label: "Flowrate Scale",
      render: (val, item) => item.flowrate?.scale ?? "-",
    },
    {
      key: "flowrateOffset",
      label: "Flowrate Offset",
      render: (val, item) => item.flowrate?.offset ?? "-",
    },
    {
      key: "created_at",
      label: "Created",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
  ];
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredData = normalizedSearch
    ? data.filter((item) =>
        [
          item.status,
          getOptionLabel(CONNECTED_STATUS_OPTIONS, item.status),
          item.name,
          item.slave_id,
          item.totalizer?.addr,
          item.totalizer?.quantity,
          getOptionLabel(MODBUS_DATA_TYPE_OPTIONS, item.totalizer?.data_type),
          getOptionLabel(MODBUS_ORDER_OPTIONS, item.totalizer?.order),
          item.totalizer?.scale,
          item.totalizer?.offset,
          item.flowrate?.addr,
          item.flowrate?.quantity,
          getOptionLabel(MODBUS_DATA_TYPE_OPTIONS, item.flowrate?.data_type),
          getOptionLabel(MODBUS_ORDER_OPTIONS, item.flowrate?.order),
          item.flowrate?.scale,
          item.flowrate?.offset,
          item.created_at,
        ].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(normalizedSearch),
        ),
      )
    : data;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-gradient-to-br from-sky-500 to-sky-600 p-2.5 text-white">
          <Activity className="h-6 w-6" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-900">Flowmeter</h2>
          <p className="text-sm text-slate-500">
            Total: <span className="font-semibold">{data.length}</span>
          </p>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search flowmeters..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <AssetTable
        data={filteredData}
        columns={columns}
        emptyMessage="No flowmeters found"
        assetType="flowmeter"
        hasActions={true}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
};

const Components = () => {
  const clientRef = useRef(null);
  const pendingConfigAckRef = useRef(null);
  // States for all assets
  const [ugTanks, setUgTanks] = useState([]);
  const [towers, setTowers] = useState([]);
  const [sources, setSources] = useState([]);
  const [flowmeters, setFlowmeters] = useState([]);
  const [pumpControllers, setPumpControllers] = useState([]);
  const [borewellPumpControllers, setBorewellPumpControllers] = useState([]);
  const [levelControllers, setLevelControllers] = useState([]);
  const [additionalSensors, setAdditionalSensors] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [valves, setValves] = useState([]);
  const [flows, setFlows] = useState([]);

  // Search states
  const [searchUg, setSearchUg] = useState("");
  const [searchTowers, setSearchTowers] = useState("");
  const [searchSources, setSearchSources] = useState("");
  const [searchFlowmeters, setSearchFlowmeters] = useState("");
  const [searchPumpCtrl, setSearchPumpCtrl] = useState("");
  const [searchBorewellPumpCtrl, setSearchBorewellPumpCtrl] = useState("");
  const [searchLevelCtrl, setSearchLevelCtrl] = useState("");
  const [searchAdditionalSensors, setSearchAdditionalSensors] = useState("");
  const [searchPumps, setSearchPumps] = useState("");
  const [searchTanks, setSearchTanks] = useState("");
  const [searchValves, setSearchValves] = useState("");

  // Loading states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [configuringDeviceId, setConfiguringDeviceId] = useState("");

  const normalizeOptionalTankSensorType = (value) =>
    value === null || value === undefined || String(value).trim() === ""
      ? ""
      : normalizeTankSensorType(value);

  const normalizeOptionalConnectionType = (value) =>
    value === null || value === undefined || String(value).trim() === ""
      ? ""
      : getLevelConnectionTypeCode(value);

  const normalizeOptionalGsmApn = (value) =>
    value === null || value === undefined || String(value).trim() === ""
      ? ""
      : getGsmApnCode(value);

  const isEditFieldVisible = (assetType, field, form = editForm) => {
    if (assetType !== "pump") return true;

    const controlType = Number(form.control_type);
    if (field.path?.startsWith("vfd.")) return isVfdPumpControlType(controlType);
    if (field.path?.startsWith("pressure_range.")) return controlType === 2;

    return true;
  };

  const editConfigs = {
    tower: {
      label: "Tower",
      fields: [
        { key: "name", label: "Name" },
        { key: "location", label: "Location" },
        { key: "active", label: "Active", type: "checkbox" },
      ],
      update: api.updateTower,
    },
    source: {
      label: "Source",
      fields: [
        { key: "source_id", label: "Source ID" },
        { key: "name", label: "Name" },
        {
          key: "type",
          label: "Type",
          type: "select",
          valueType: "number",
          options: [
            { value: "0", label: "Borewell" },
            { value: "1", label: "Tank" },
          ],
        },
        {
          key: "status",
          label: "Status",
          type: "select",
          valueType: "number",
          min: 0,
          max: 1,
          options: SOURCE_STATUS_OPTIONS,
        },
        {
          key: "sensor_type",
          label: "Sensor Type",
          type: "select",
          valueType: "number",
          min: 1,
          max: 4,
          options: SOURCE_SENSOR_TYPE_OPTIONS,
        },
        {
          key: "sensor_slave_id",
          label: "Sensor Slave ID",
          type: "number",
          min: 0,
          max: 500,
          integer: true,
        },
        {
          key: "cable_length_m",
          label: "Cable Length (m)",
          type: "number",
          min: 0,
        },
        {
          key: "tank_height",
          label: "Tank Height",
          type: "number",
          min: 0,
        },
        {
          key: "low",
          label: "Low",
          type: "number",
          min: 0,
          max: 100,
          integer: true,
        },
        {
          key: "med",
          label: "Mid",
          type: "number",
          min: 0,
          max: 100,
          integer: true,
        },
        {
          key: "high",
          label: "High",
          type: "number",
          min: 0,
          max: 100,
          integer: true,
        },
        { key: "publish", label: "Publish", type: "checkbox" },
        { key: "location", label: "Location" },
      ],
      update: api.updateSource,
    },
    flowmeter: {
      label: "Flowmeter",
      fields: [
        { key: "name", label: "Name" },
        {
          key: "status",
          label: "Flowmeter Status",
          type: "select",
          valueType: "number",
          min: 0,
          max: 1,
          integer: true,
          options: CONNECTED_STATUS_OPTIONS,
        },
        {
          key: "slave_id",
          label: "Flowmeter Slave ID",
          type: "number",
          min: 0,
          max: 500,
          integer: true,
        },
        {
          key: "totalizerAddr",
          path: "totalizer.addr",
          label: "Totalizer Address",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "totalizerQuantity",
          path: "totalizer.quantity",
          label: "Totalizer Quantity",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "totalizerDataType",
          path: "totalizer.data_type",
          label: "Totalizer Data Type",
          type: "select",
          valueType: "number",
          min: 1,
          max: 6,
          options: MODBUS_DATA_TYPE_OPTIONS,
        },
        {
          key: "totalizerOrder",
          path: "totalizer.order",
          label: "Totalizer Order",
          type: "select",
          valueType: "number",
          min: 0,
          max: 4,
          options: MODBUS_ORDER_OPTIONS,
        },
        {
          key: "totalizerScale",
          path: "totalizer.scale",
          label: "Totalizer Scale",
          type: "number",
        },
        {
          key: "totalizerOffset",
          path: "totalizer.offset",
          label: "Totalizer Offset",
          type: "number",
        },
        {
          key: "flowrateAddr",
          path: "flowrate.addr",
          label: "Flowrate Address",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "flowrateQuantity",
          path: "flowrate.quantity",
          label: "Flowrate Quantity",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "flowrateDataType",
          path: "flowrate.data_type",
          label: "Flowrate Data Type",
          type: "select",
          valueType: "number",
          min: 1,
          max: 6,
          options: MODBUS_DATA_TYPE_OPTIONS,
        },
        {
          key: "flowrateOrder",
          path: "flowrate.order",
          label: "Flowrate Order",
          type: "select",
          valueType: "number",
          min: 0,
          max: 4,
          options: MODBUS_ORDER_OPTIONS,
        },
        {
          key: "flowrateScale",
          path: "flowrate.scale",
          label: "Flowrate Scale",
          type: "number",
        },
        {
          key: "flowrateOffset",
          path: "flowrate.offset",
          label: "Flowrate Offset",
          type: "number",
        },
      ],
      update: api.updateFlowmeter,
    },
    ug_tank: {
      label: "UG Tank",
      fields: [
        { key: "ug_tank_id", label: "Tank ID" },
        { key: "name", label: "Name" },
        { key: "capacity", label: "Capacity", type: "number" },
        { key: "height", label: "Height", type: "number" },
        {
          key: "low",
          label: "Low",
          type: "number",
          min: 0,
          max: 100,
          integer: true,
        },
        {
          key: "med",
          label: "Mid",
          type: "number",
          min: 0,
          max: 100,
          integer: true,
        },
        {
          key: "high",
          label: "High",
          type: "number",
          min: 0,
          max: 100,
          integer: true,
        },
        {
          key: "sensor_type",
          label: "Sensor Type",
          type: "select",
          options: TANK_SENSOR_TYPE_OPTIONS,
          normalize: normalizeOptionalTankSensorType,
        },
        {
          key: "sensor_slave_id",
          label: "Sensor Slave ID",
          type: "number",
          min: 1,
          max: 500,
        },
        { key: "bme_offset", label: "BME Offset", type: "number" },
        { key: "publish", label: "Publish", type: "checkbox" },
        { key: "control", label: "Control", type: "checkbox" },
      ],
      update: api.updateUGTank,
    },
    atbc: {
      label: "Pump Controller",
      fields: [
        { key: "device_id", label: "Device ID" },
        { key: "name", label: "Name" },
        {
          key: "connectionType",
          path: "network.connectionType",
          label: "Connection Type",
          type: "select",
          valueType: "number",
          options: LEVEL_CONNECTION_TYPE_OPTIONS,
          normalize: normalizeOptionalConnectionType,
        },
        {
          key: "gsmISP",
          path: "network.gsmISP",
          label: "GSM ISP",
          type: "select",
          valueType: "number",
          options: GSM_APN_OPTIONS,
          normalize: normalizeOptionalGsmApn,
        },
        {
          key: "wifiUsn",
          path: "network.wifiUsn",
          label: "WiFi Username",
        },
        {
          key: "wifiPwd",
          path: "network.wifiPwd",
          label: "WiFi Password",
        },
      ],
      update: api.updateATBC,
    },
    atbpc: {
      label: "Borewell Pump Ctrl",
      fields: [
        { key: "device_id", label: "Device ID" },
        { key: "name", label: "Name" },
        {
          key: "connectionType",
          path: "network.connectionType",
          label: "Connection Type",
          type: "select",
          valueType: "number",
          options: LEVEL_CONNECTION_TYPE_OPTIONS,
          normalize: normalizeOptionalConnectionType,
        },
        {
          key: "gsmISP",
          path: "network.gsmISP",
          label: "GSM ISP",
          type: "select",
          valueType: "number",
          options: GSM_APN_OPTIONS,
          normalize: normalizeOptionalGsmApn,
        },
        {
          key: "wifiUsn",
          path: "network.wifiUsn",
          label: "WiFi Username",
        },
        {
          key: "wifiPwd",
          path: "network.wifiPwd",
          label: "WiFi Password",
        },
      ],
      update: api.updateBorewellPumpController,
    },
    aquatank: {
      label: "Level Controller",
      fields: [
        { key: "device_id", label: "Device ID" },
        { key: "name", label: "Name" },
        {
          key: "connectionType",
          path: "network.connectionType",
          label: "Connection Type",
          type: "select",
          valueType: "number",
          options: LEVEL_CONNECTION_TYPE_OPTIONS,
          normalize: normalizeOptionalConnectionType,
        },
        {
          key: "gsmISP",
          path: "network.gsmISP",
          label: "GSM ISP",
          type: "number",
        },
        {
          key: "wifiUsn",
          path: "network.wifiUsn",
          label: "WiFi Username",
        },
        {
          key: "wifiPwd",
          path: "network.wifiPwd",
          label: "WiFi Password",
        },
      ],
      update: api.updateAquaTank,
    },
    pump: {
      label: "Pump",
      fields: [
        { key: "pump_id", label: "Pump ID" },
        { key: "name", label: "Name" },
        { key: "power_rating", label: "Power Rating", type: "number" },
        { key: "model", label: "Model" },
        {
          key: "energyMeterStatus",
          path: "energymeter.status",
          label: "Energy Meter Status",
          type: "select",
          valueType: "number",
          options: CONNECTED_STATUS_OPTIONS,
        },
        {
          key: "energyMeterMake",
          path: "energymeter.make",
          label: "Energy Meter Make",
          type: "select",
          valueType: "number",
          min: 1,
          max: 1,
          options: ENERGY_METER_MAKE_OPTIONS,
          normalize: normalizeEnergyMeterMake,
        },
        {
          key: "energyMeterSlaveId",
          path: "energymeter.slave_id",
          label: "Energy Meter Slave ID",
          type: "number",
          min: 1,
          max: 500,
        },
        {
          key: "control_type",
          label: "Control Type",
          type: "select",
          valueType: "number",
          min: 0,
          max: 3,
          options: PUMP_CONTROL_TYPE_OPTIONS,
        },
        {
          key: "vfdSlaveId",
          path: "vfd.slave_id",
          label: "VFD Slave ID",
          type: "number",
          min: 0,
          max: 500,
          integer: true,
        },
        {
          key: "vfdPressureSlaveId",
          path: "vfd.pressure_slave_id",
          label: "Pressure Slave ID",
          type: "number",
          min: 0,
          max: 500,
          integer: true,
        },
        {
          key: "pressureStartAddress",
          path: "vfd.pressure_start_address",
          label: "Pressure Start Address",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "pressureQuantity",
          path: "vfd.pressure_quantity",
          label: "Pressure Quantity",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "pressureDataType",
          path: "vfd.pressure_data_type",
          label: "Pressure Data Type",
          type: "select",
          valueType: "number",
          min: 1,
          max: 6,
          options: MODBUS_DATA_TYPE_OPTIONS,
        },
        {
          key: "pressureOrder",
          path: "vfd.pressure_order",
          label: "Pressure Order",
          type: "select",
          valueType: "number",
          min: 0,
          max: 4,
          options: MODBUS_ORDER_OPTIONS,
        },
        {
          key: "pressureScale",
          path: "vfd.pressure_scale",
          label: "Pressure Scale",
          type: "number",
        },
        {
          key: "pressureOffset",
          path: "vfd.pressure_offset",
          label: "Pressure Offset",
          type: "number",
        },
        {
          key: "rangeLowBar",
          path: "vfd.range_low_bar",
          label: "Range Low Bar",
          type: "number",
        },
        {
          key: "rangeHighBar",
          path: "vfd.range_high_bar",
          label: "Range High Bar",
          type: "number",
        },
        {
          key: "minFreqHz",
          path: "vfd.min_freq_hz",
          label: "Min Freq Hz",
          type: "number",
        },
        {
          key: "maxFreqHz",
          path: "vfd.max_freq_hz",
          label: "Max Freq Hz",
          type: "number",
        },
        {
          key: "pressureRangeIsSource",
          path: "pressure_range.is_source",
          label: "Pressure Range Is Source",
          type: "checkbox",
        },
        {
          key: "pressureRangePtId",
          path: "pressure_range.pt_id",
          label: "Pressure Range PT ID",
        },
        {
          key: "pressureRangeStageIndex",
          path: "pressure_range.stage_index",
          label: "Pressure Stage Index",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "pressureRangePtSlaveId",
          path: "pressure_range.pt_slave_id",
          label: "Pressure PT Slave ID",
          type: "number",
          min: 0,
          max: 500,
          integer: true,
        },
        {
          key: "pressureRangePtStartAddress",
          path: "pressure_range.pt_start_address",
          label: "Pressure PT Start Address",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "pressureRangePtQuantity",
          path: "pressure_range.pt_quantity",
          label: "Pressure PT Quantity",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "pressureRangePtDataType",
          path: "pressure_range.pt_data_type",
          label: "Pressure PT Data Type",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "pressureRangePtOrder",
          path: "pressure_range.pt_order",
          label: "Pressure PT Order",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "pressureRangePtScale",
          path: "pressure_range.pt_scale",
          label: "Pressure PT Scale",
          type: "number",
        },
        {
          key: "pressureRangePtOffset",
          path: "pressure_range.pt_offset",
          label: "Pressure PT Offset",
          type: "number",
        },
        {
          key: "pressureRangeLowBar",
          path: "pressure_range.range_low_bar",
          label: "Pressure Range Low Bar",
          type: "number",
        },
        {
          key: "pressureRangeHighBar",
          path: "pressure_range.range_high_bar",
          label: "Pressure Range High Bar",
          type: "number",
        },
        { key: "control", label: "Control", type: "checkbox" },
      ],
      update: api.updatePump,
    },
    tank: {
      label: "Tank",
      fields: [
        { key: "tank_id", label: "Tank ID" },
        { key: "name", label: "Name" },
        { key: "capacity", label: "Capacity", type: "number" },
        {
          key: "sensor_type",
          label: "Sensor Type",
          type: "select",
          options: TANK_SENSOR_TYPE_OPTIONS,
          normalize: normalizeOptionalTankSensorType,
        },
        {
          key: "sensor_slave_id",
          label: "Sensor Slave ID",
          type: "number",
          min: 1,
          max: 500,
        },
        { key: "low", label: "Low", type: "number", min: 0, max: 100 },
        { key: "med", label: "Med", type: "number", min: 0, max: 100 },
        { key: "high", label: "High", type: "number", min: 0, max: 100 },
        { key: "height", label: "Height", type: "number" },
        { key: "bme_offset", label: "BME Offset", type: "number" },
        { key: "publish", label: "Publish", type: "checkbox" },
        {
          key: "cluster_count",
          label: "Cluster Count",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "cluster_slave_id_start",
          label: "Cluster Slave ID Start",
          type: "number",
          min: 0,
          max: 500,
          integer: true,
        },
        { key: "control", label: "Control", type: "checkbox" },
      ],
      update: api.updateTank,
    },
    valve: {
      label: "Valve",
      fields: [
        { key: "valve_id", label: "Valve ID" },
        {
          key: "type",
          label: "Type",
          type: "select",
          options: [
            { value: "motorized", label: "Motorized" },
            { value: "manual", label: "Manual" },
          ],
        },
        { key: "control", label: "Control", type: "checkbox" },
      ],
      update: api.updateValve,
    },
    additional_sensor: {
      label: "Additional Sensor",
      fields: [
        { key: "s_id", label: "Sensor ID" },
        { key: "sensor_name", label: "Sensor Name" },
        { key: "unit", label: "Unit" },
        { key: "device_id", label: "Device ID" },
        {
          key: "slave_id",
          label: "Slave ID",
          type: "number",
          min: 1,
          max: 500,
          integer: true,
        },
        {
          key: "start_address",
          label: "Start Address",
          type: "number",
          min: 0,
          integer: true,
        },
        {
          key: "quantity",
          label: "Quantity",
          type: "number",
          min: 1,
          integer: true,
        },
        {
          key: "data_type",
          label: "Data Type",
          type: "select",
          valueType: "number",
          min: 1,
          max: 6,
          options: MODBUS_DATA_TYPE_OPTIONS,
        },
        {
          key: "order",
          label: "Order",
          type: "select",
          valueType: "number",
          min: 0,
          max: 4,
          options: MODBUS_ORDER_OPTIONS,
        },
        { key: "scale", label: "Scale", type: "number" },
        { key: "offset", label: "Offset", type: "number" },
      ],
      update: api.updateAdditionalSensor,
    },
  };

  // Fetch all assets
  const fetchAllAssets = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const [
        ugTanksRes,
        towersRes,
        sourcesRes,
        flowmetersRes,
        pumpControllersRes,
        borewellPumpControllersRes,
        levelControllersRes,
        additionalSensorsRes,
        pumpsRes,
        tanksRes,
        valvesRes,
        flowsRes,
      ] = await Promise.all([
        api.getUGTanks().catch(() => ({ data: [] })),
        api.getTowers().catch(() => ({ data: [] })),
        api.getSources().catch(() => ({ data: [] })),
        api.getFlowmeters().catch(() => ({ data: [] })),
        api.getPumpControllers().catch(() => ({ data: [] })),
        api.getBorewellPumpControllers().catch(() => ({ data: [] })),
        api.getLevelControllers().catch(() => ({ data: [] })),
        api.getAdditionalSensors().catch(() => ({ data: [] })),
        api.getPumps().catch(() => ({ data: [] })),
        api.getTanks().catch(() => ({ data: [] })),
        api.getValves().catch(() => ({ data: [] })),
        api.getFlows().catch(() => ({ data: [] })),
      ]);

      console.log(flowsRes, "((((()))))))");

      setUgTanks(ugTanksRes.data || []);
      setTowers(towersRes.data || []);
      setSources(sourcesRes.data || []);
      setFlowmeters(flowmetersRes.data || []);
      setPumpControllers(pumpControllersRes.data || []);
      setBorewellPumpControllers(borewellPumpControllersRes.data || []);
      setLevelControllers(levelControllersRes.data || []);
      setAdditionalSensors(additionalSensorsRes.data || []);
      setPumps(pumpsRes.data || []);
      setTanks(tanksRes.data || []);
      setValves(valvesRes.data || []);
      setFlows(flowsRes || []);

      if (isRefresh) {
        toast.success("Assets refreshed successfully");
      }
    } catch (error) {
      toast.error("Failed to load assets");
      console.error(error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getNumberOrDefault = (value, defaultValue) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : defaultValue;
  };

  const getBooleanOrDefault = (value, defaultValue) =>
    typeof value === "boolean" ? value : defaultValue;

  const buildConfigMessageId = (prefix = "cfg") =>
    `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

  const getLevelControllerNetwork = (deviceId) => {
    const controller = levelControllers.find(
      (item) => item.device_id === deviceId,
    );
    const network = controller?.network || {};

    return {
      connectionType: getLevelConnectionTypeCode(network.connectionType),
      gsmISP: getNumberOrDefault(network.gsmISP, DEFAULT_GSM_APN_CODE),
      wifiUsn: network.wifiUsn || "",
      wifiPwd: network.wifiPwd || "",
    };
  };

  const getPumpControllerNetwork = (deviceId) => {
    const controller = pumpControllers.find(
      (item) => item.device_id === deviceId,
    );
    const network = controller?.network || {};

    return {
      connection_type: getLevelConnectionTypeCode(network.connectionType),
      gsm_isp: getGsmApnCode(network.gsmISP),
      wifi_username: network.wifiUsn || "",
      wifi_password: network.wifiPwd || "",
    };
  };

  const getATBPCNetwork = (deviceId) => {
    const controller = borewellPumpControllers.find(
      (item) => item.device_id === deviceId,
    );
    const network = controller?.network || {};

    return {
      connection_type: getLevelConnectionTypeCode(network.connectionType),
      gsm_isp: getGsmApnCode(network.gsmISP),
      wifi_username: network.wifiUsn || "",
      wifi_password: network.wifiPwd || "",
    };
  };

  const cleanFlowRef = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value).trim();
    return ["", "-", "null", "none", "undefined"].includes(text.toLowerCase())
      ? ""
      : text;
  };

  const uniqueFlowRefs = (values = []) =>
    values.reduce((refs, value) => {
      const cleaned = cleanFlowRef(value);
      return cleaned && !refs.includes(cleaned) ? [...refs, cleaned] : refs;
    }, []);

  const buildAdditionalSensorConfigs = (deviceId) =>
    additionalSensors
      .filter((sensor) => sensor.device_id === deviceId)
      .map((sensor) => ({
        id: sensor.s_id,
        slave_id: getNumberOrDefault(sensor.slave_id, 0),
        start_address: getNumberOrDefault(sensor.start_address, 0),
        quantity: getNumberOrDefault(sensor.quantity, 0),
        data_type: getNumberOrDefault(sensor.data_type, 0),
        order: getNumberOrDefault(sensor.order, 0),
        scale: getNumberOrDefault(sensor.scale, 1),
        offset: getNumberOrDefault(sensor.offset, 0),
      }));

  const buildPumpVfdConfig = (pumpMeta = {}) => {
    const vfd = pumpMeta.vfd || {};

    return {
      slave_id: getNumberOrDefault(vfd.slave_id, 0),
      pressure_slave_id: getNumberOrDefault(vfd.pressure_slave_id, 0),
      pressure_start_address: getNumberOrDefault(vfd.pressure_start_address, 0),
      pressure_quantity: getNumberOrDefault(vfd.pressure_quantity, 0),
      pressure_data_type: getNumberOrDefault(vfd.pressure_data_type, 0),
      pressure_order: getNumberOrDefault(vfd.pressure_order, 0),
      pressure_scale: getNumberOrDefault(vfd.pressure_scale, 1),
      pressure_offset: getNumberOrDefault(vfd.pressure_offset, 0),
      range_low_bar: getNumberOrDefault(vfd.range_low_bar, 0),
      range_high_bar: getNumberOrDefault(vfd.range_high_bar, 0),
      min_freq_hz: getNumberOrDefault(vfd.min_freq_hz, 0),
      max_freq_hz: getNumberOrDefault(vfd.max_freq_hz, 0),
    };
  };

  const buildPumpPressureRangeConfig = (pumpMeta = {}) => {
    const pressureRange = pumpMeta.pressure_range || {};

    return {
      is_source: getBooleanOrDefault(pressureRange.is_source, true),
      pt_id: pressureRange.pt_id || "-",
      stage_index: getNumberOrDefault(pressureRange.stage_index, 0),
      pt_slave_id: getNumberOrDefault(pressureRange.pt_slave_id, 0),
      pt_start_address: getNumberOrDefault(pressureRange.pt_start_address, 0),
      pt_quantity: getNumberOrDefault(pressureRange.pt_quantity, 0),
      pt_data_type: getNumberOrDefault(pressureRange.pt_data_type, 0),
      pt_order: getNumberOrDefault(pressureRange.pt_order, 0),
      pt_scale: getNumberOrDefault(pressureRange.pt_scale, 1),
      pt_offset: getNumberOrDefault(pressureRange.pt_offset, 0),
      range_low_bar: getNumberOrDefault(pressureRange.range_low_bar, 0),
      range_high_bar: getNumberOrDefault(pressureRange.range_high_bar, 0),
    };
  };

  const buildPumpEnergyMeterConfig = (pumpMeta = {}) => {
    const energyMeter = pumpMeta.energymeter || {};

    return {
      status: getNumberOrDefault(energyMeter.status, 1),
      make: getNumberOrDefault(energyMeter.make, 0),
      slave_id: getNumberOrDefault(energyMeter.slave_id, 0),
    };
  };

  // ATBPC d_config destination tank: field names and order follow the device
  // packet spec exactly (no low/med/high thresholds here).
  const buildATBPCDestinationTankConfig = (tankMeta = {}) => ({
    tank_id: tankMeta.tank_id || tankMeta.ug_tank_id,
    status:
      tankMeta.status !== undefined
        ? getNumberOrDefault(tankMeta.status, 0)
        : getBooleanOrDefault(tankMeta.control, true)
          ? 1
          : 0,
    tank_height: getNumberOrDefault(tankMeta.height, 0),
    sensor_type: getTankSensorTypeCode(tankMeta.sensor_type),
    sensor_slave_id: getNumberOrDefault(tankMeta.sensor_slave_id, 0),
    bme_offset: getNumberOrDefault(tankMeta.bme_offset, 0),
    publish: getBooleanOrDefault(tankMeta.publish, false),
  });

  // The device always expects tank1 and tank2; a pump maps to one
  // destination tank, so tank2 is sent disabled.
  const EMPTY_ATBPC_DESTINATION_TANK = {
    tank_id: "-",
    status: 0,
    tank_height: 0,
    sensor_type: 0,
    sensor_slave_id: 0,
    bme_offset: 0,
    publish: false,
  };

  const buildATBPCDestinationConfig = (tankMeta = {}) => ({
    tank1: buildATBPCDestinationTankConfig(tankMeta),
    tank2: { ...EMPTY_ATBPC_DESTINATION_TANK },
  });

  const buildATBPCSourceConfig = (sourceMeta = {}) => {
    if (sourceMeta.source_id) {
      return {
        type: getNumberOrDefault(sourceMeta.type, 0),
        id: sourceMeta.source_id,
        status: getNumberOrDefault(sourceMeta.status, 0),
        sensor_type: getNumberOrDefault(sourceMeta.sensor_type, 0),
        sensor_slave_id: getNumberOrDefault(sourceMeta.sensor_slave_id, 0),
        cable_length_m: getNumberOrDefault(sourceMeta.cable_length_m, 0),
        tank_height: getNumberOrDefault(sourceMeta.tank_height, 0),
        publish: getBooleanOrDefault(sourceMeta.publish, false),
      };
    }

    return {
      type: 1,
      id: sourceMeta.tank_id || sourceMeta.ug_tank_id,
      status:
        sourceMeta.status !== undefined
          ? getNumberOrDefault(sourceMeta.status, 0)
          : 1,
      sensor_type: getTankSensorTypeCode(sourceMeta.sensor_type),
      sensor_slave_id: getNumberOrDefault(sourceMeta.sensor_slave_id, 0),
      cable_length_m: 0,
      tank_height: getNumberOrDefault(sourceMeta.height, 0),
      publish: getBooleanOrDefault(sourceMeta.publish, false),
    };
  };

  const buildFlowmeterRegisterConfig = (registerConfig = {}) => ({
    addr: getNumberOrDefault(registerConfig.addr, 0),
    quantity: getNumberOrDefault(registerConfig.quantity, 0),
    data_type: getNumberOrDefault(registerConfig.data_type, 0),
    order: getNumberOrDefault(registerConfig.order, 0),
    scale: getNumberOrDefault(registerConfig.scale, 1),
    offset: getNumberOrDefault(registerConfig.offset, 0),
  });

  const buildATBPCFlowmeterConfig = (flowmeterMeta = {}) => ({
    status: getNumberOrDefault(flowmeterMeta.status, 0),
    slave_id: getNumberOrDefault(flowmeterMeta.slave_id, 0),
    totalizer: buildFlowmeterRegisterConfig(flowmeterMeta.totalizer),
    flowrate: buildFlowmeterRegisterConfig(flowmeterMeta.flowrate),
  });

  const getFlowLevelControllerMappings = (flow = {}) => {
    const nested = Array.isArray(flow.level_controller_mappings)
      ? flow.level_controller_mappings
          .map((entry) => ({
            level_controller_id: cleanFlowRef(entry.level_controller_id),
            valve_tank_mappings: (entry.valve_tank_mappings || []).filter(
              (mapping) =>
                cleanFlowRef(mapping.valve_id) ||
                cleanFlowRef(mapping.tank_id) ||
                cleanFlowRef(mapping.pump_id) ||
                getMappingPumps(mapping).length > 0,
            ),
          }))
          .filter(
            (entry) =>
              entry.level_controller_id || entry.valve_tank_mappings.length > 0,
          )
      : [];

    if (nested.length > 0) return nested;

    const levelControllerId = cleanFlowRef(flow.level_controller_id);
    const mappings = (flow.valve_tank_mappings || []).filter(
      (mapping) =>
        cleanFlowRef(mapping.valve_id) ||
        cleanFlowRef(mapping.tank_id) ||
        cleanFlowRef(mapping.pump_id),
    );

    return levelControllerId || mappings.length > 0
      ? [
          {
            level_controller_id: levelControllerId,
            valve_tank_mappings: mappings,
          },
        ]
      : [];
  };

  // Pumps connected to a tank mapping as [{ pump_id, pump_controller_id }].
  // A tank can be fed by several pumps; older mappings hold a single pump_id.
  const getMappingPumps = (mapping = {}) => {
    const pumps = [];
    (Array.isArray(mapping.pumps) ? mapping.pumps : []).forEach((item) => {
      const pumpId = cleanFlowRef(item?.pump_id);
      if (!pumpId || pumps.some((pump) => pump.pump_id === pumpId)) return;
      pumps.push({
        pump_id: pumpId,
        pump_controller_id: cleanFlowRef(item?.pump_controller_id),
      });
    });
    if (pumps.length === 0 && cleanFlowRef(mapping.pump_id)) {
      pumps.push({
        pump_id: cleanFlowRef(mapping.pump_id),
        pump_controller_id: cleanFlowRef(mapping.pump_controller_id),
      });
    }
    return pumps;
  };

  const getFlowPumpControllerIds = (flow = {}) =>
    uniqueFlowRefs([
      ...(Array.isArray(flow.pump_controller_ids)
        ? flow.pump_controller_ids
        : []),
      flow.pump_controller_id,
      ...getFlowLevelControllerMappings(flow).flatMap((entry) =>
        (entry.valve_tank_mappings || []).flatMap((mapping) => [
          mapping.pump_controller_id,
          ...getMappingPumps(mapping).map((pump) => pump.pump_controller_id),
        ]),
      ),
    ]);

  const getFlowATBPCConfig = (flow = {}) => {
    const config = flow.atbpc_config || {};
    const pumpConfigurations = Array.isArray(config.pump_configurations)
      ? config.pump_configurations
          .map((mapping) => ({
            pump_id: cleanFlowRef(mapping.pump_id),
            destination_tank_id: cleanFlowRef(mapping.destination_tank_id),
            source_id: cleanFlowRef(mapping.source_id),
            flowmeter_id: cleanFlowRef(mapping.flowmeter_id),
          }))
          .filter((mapping) => Object.values(mapping).some(Boolean))
      : [];

    return {
      atbpc_device_id: cleanFlowRef(config.atbpc_device_id),
      pump_configurations: pumpConfigurations,
    };
  };

  const findATBPCDestinationMeta = (destinationId, societyId) =>
    tanks.find(
      (tank) =>
        tank.tank_id === destinationId &&
        (!tank.society_id || tank.society_id === societyId),
    ) ||
    ugTanks.find(
      (tank) =>
        tank.ug_tank_id === destinationId &&
        (!tank.society_id || tank.society_id === societyId),
    );

  const findATBPCSourceMeta = (sourceId, societyId) =>
    sources.find(
      (source) =>
        source.source_id === sourceId &&
        (!source.society_id || source.society_id === societyId),
    ) ||
    tanks.find(
      (tank) =>
        tank.tank_id === sourceId &&
        (!tank.society_id || tank.society_id === societyId),
    ) ||
    ugTanks.find(
      (tank) =>
        tank.ug_tank_id === sourceId &&
        (!tank.society_id || tank.society_id === societyId),
    );

  const publishConfigWithAck = (config, successMessage) => {
    const client = clientRef.current;
    if (!client || !client.connected) {
      toast.error("MQTT not connected");
      return;
    }
    if (pendingConfigAckRef.current) {
      toast.error("A device configuration request is already in progress");
      return;
    }

    const topic = `${MQTT_C2D_PREFIX}/${config.device_id}`;
    const toastId = CONFIG_ACK_TOAST_ID;
    toast.dismiss(toastId);
    toast.loading("Waiting for device acknowledgement... 0s / 30s", {
      id: toastId,
    });
    let settled = false;
    const startedAt = Date.now();

    const cleanup = () => {
      const pending = pendingConfigAckRef.current;
      if (pending?.intervalId) clearInterval(pending.intervalId);
      if (pending?.timeoutId) clearTimeout(pending.timeoutId);
      pendingConfigAckRef.current = null;
      setConfiguringDeviceId("");
    };

    const settle = (status, error) => {
      if (settled) return;
      settled = true;
      cleanup();
      toast.dismiss(toastId);

      if (status === "pass") {
        toast.success(successMessage);
      } else if (status === "timeout") {
        toast.error("Request timed out");
      } else if (status === "publish_error") {
        console.error("Publish failed:", error);
        toast.error("Failed to send config");
      } else {
        toast.error("Device configuration rejected");
      }
    };

    const intervalId = setInterval(() => {
      const elapsed = Math.min(30, Math.floor((Date.now() - startedAt) / 1000));
      toast.loading(`Waiting for device acknowledgement... ${elapsed}s / 30s`, {
        id: toastId,
      });
    }, 1000);

    const timeoutId = setTimeout(() => {
      toast.loading("Waiting for device acknowledgement... 30s / 30s", {
        id: toastId,
      });
      settle("timeout");
    }, 30000);

    setConfiguringDeviceId(config.device_id);
    pendingConfigAckRef.current = {
      device_id: config.device_id,
      device_type: config.device_type,
      config_type: config.config_type,
      intervalId,
      timeoutId,
      handleAck: (payload) => {
        settle(normalizeConfigResult(payload.set_config_result));
      },
    };

    client.publish(topic, JSON.stringify(config), { qos: 1 }, (err) => {
      if (err) {
        settle("publish_error", err);
      }
    });
  };

  const sendPumpConfig = (deviceId) => {
    // ✅ Step 1: find pumps for this controller
    const matchedFlows = flows.filter((item) =>
      getFlowPumpControllerIds(item).includes(deviceId),
    );

    if (matchedFlows.length === 0) {
      toast.error("This pump controller is not added to any flows");
      return;
    }

    // ✅ Step 2: build UG tank → tanks mapping
    const pumpMap = new Map();

    matchedFlows.forEach((flow) => {
      const flowPumpControllerIds = getFlowPumpControllerIds(flow);
      getFlowLevelControllerMappings(flow).forEach((entry) => {
        (entry.valve_tank_mappings || []).forEach((mapping) => {
          getMappingPumps(mapping).forEach((pumpRef) => {
            if (
              pumpRef.pump_controller_id &&
              pumpRef.pump_controller_id !== deviceId
            ) {
              return;
            }
            if (!pumpRef.pump_controller_id && flowPumpControllerIds.length > 1) {
              return;
            }

            const pumpId = pumpRef.pump_id;

            if (!pumpMap.has(pumpId)) {
              pumpMap.set(pumpId, {
                pump_id: pumpId,
                associated_tanks: [],
                associated_ugts: [],
              });
            }

            const pumpConfig = pumpMap.get(pumpId);
            if (
              mapping.tank_id &&
              !pumpConfig.associated_tanks.includes(mapping.tank_id)
            ) {
              pumpConfig.associated_tanks.push(mapping.tank_id);
            }
            if (
              flow.ug_tank_id &&
              !pumpConfig.associated_ugts.includes(flow.ug_tank_id)
            ) {
              pumpConfig.associated_ugts.push(flow.ug_tank_id);
            }
          });
        });
      });
    });

    const pumpConfigs = Array.from(pumpMap.values());
    if (pumpConfigs.length === 0) {
      toast.error("No pumps are mapped to this pump controller");
      return;
    }

    // ✅ Step 3: build final config
    const config = {
      config_type: "d_config",
      device_type: "p_controller",
      device_id: deviceId,
      mid: buildConfigMessageId("req"),
      society_id: matchedFlows[0].society_id,
      network: getPumpControllerNetwork(deviceId),
    };

    const missingControlConfigPump = pumpConfigs.find((pump) => {
      const pumpMeta = pumps.find((item) => item.pump_id === pump.pump_id);
      const controlType = getNumberOrDefault(pumpMeta?.control_type, 0);

      if (isVfdPumpControlType(controlType)) return !pumpMeta?.vfd;
      if (controlType === 2) return !pumpMeta?.pressure_range;

      return false;
    });

    if (missingControlConfigPump) {
      const pumpMeta = pumps.find(
        (item) => item.pump_id === missingControlConfigPump.pump_id,
      );
      const controlType = getNumberOrDefault(pumpMeta?.control_type, 0);
      const requiredBlock = isVfdPumpControlType(controlType)
        ? "VFD"
        : "pressure range";

      toast.error(
        `Pump ${missingControlConfigPump.pump_id} is missing saved ${requiredBlock} settings`,
      );
      return;
    }

    pumpConfigs.forEach((pump, index) => {
      const ugTankId = pump.associated_ugts[0] || "-";
      const ugTankMeta = ugTanks.find((tank) => tank.ug_tank_id === ugTankId);
      const pumpMeta = pumps.find((item) => item.pump_id === pump.pump_id);
      const energyMeter = pumpMeta?.energymeter || {};
      const controlType = getNumberOrDefault(pumpMeta?.control_type, 0);

      const pumpConfig = {
        pump_id: pump.pump_id,
        associated_ugt: ugTankId,
        ugt_tank_height: getNumberOrDefault(ugTankMeta?.height, 0),
        ugt_low: ugTankId === "-" ? 0 : getNumberOrDefault(ugTankMeta?.low, 0),
        ugt_med: ugTankId === "-" ? 0 : getNumberOrDefault(ugTankMeta?.med, 0),
        ugt_high:
          ugTankId === "-" ? 0 : getNumberOrDefault(ugTankMeta?.high, 0),
        ugt_sensor_type:
          ugTankId === "-" ? 0 : getTankSensorTypeCode(ugTankMeta?.sensor_type),
        ugt_sensor_slave_id:
          ugTankId === "-"
            ? 0
            : getNumberOrDefault(ugTankMeta?.sensor_slave_id, 0),
        ugt_publish: ugTankId !== "-",
        tank_count: pump.associated_tanks.length,
        associated_tanks: pump.associated_tanks,
        energymeter: {
          status: getNumberOrDefault(energyMeter.status, 1),
          make: getNumberOrDefault(energyMeter.make, 0),
          slave_id: getNumberOrDefault(energyMeter.slave_id, 0),
        },
        control_type: controlType,
      };

      if (isVfdPumpControlType(controlType)) {
        pumpConfig.vfd = buildPumpVfdConfig(pumpMeta);
      } else if (controlType === 2) {
        pumpConfig.pressure_range = buildPumpPressureRangeConfig(pumpMeta);
      }

      config[`pump${index + 1}`] = pumpConfig;
    });
    config.additional_sensors = buildAdditionalSensorConfigs(deviceId);
    console.log(config, "Sending config to pump controller");
    publishConfigWithAck(config, "Pump controller configured successfully");
  };

  const sendATBPCConfig = (deviceId) => {
    const matchedFlows = flows.filter(
      (flow) => getFlowATBPCConfig(flow).atbpc_device_id === deviceId,
    );

    if (matchedFlows.length === 0) {
      toast.error("This borewell pump controller is not added to any flows");
      return;
    }

    const pumpMappings = matchedFlows.flatMap((flow) =>
      getFlowATBPCConfig(flow).pump_configurations.map((mapping) => ({
        flow,
        mapping,
      })),
    );

    if (pumpMappings.length === 0) {
      toast.error("No pump configurations are mapped to this ATBPC");
      return;
    }

    for (let index = 0; index < pumpMappings.length; index += 1) {
      const { mapping } = pumpMappings[index];
      const rowNumber = index + 1;

      if (
        !mapping.pump_id ||
        !mapping.destination_tank_id ||
        !mapping.source_id
      ) {
        toast.error(`ATBPC pump configuration ${rowNumber} is incomplete`);
        return;
      }
    }

    const config = {
      device_id: deviceId,
      society_id: matchedFlows[0].society_id,
      config_type: "d_config",
      device_type: "b_controller",
      mid: String(Math.floor(1000 + Math.random() * 9000)),
      network: getATBPCNetwork(deviceId),
    };

    for (let index = 0; index < pumpMappings.length; index += 1) {
      const { mapping, flow } = pumpMappings[index];
      const pumpMeta = pumps.find(
        (pump) =>
          pump.pump_id === mapping.pump_id &&
          (!pump.society_id || pump.society_id === flow.society_id),
      );
      const tankMeta = findATBPCDestinationMeta(
        mapping.destination_tank_id,
        flow.society_id,
      );
      const sourceMeta = findATBPCSourceMeta(
        mapping.source_id,
        flow.society_id,
      );
      const flowmeterMeta = mapping.flowmeter_id
        ? flowmeters.find(
            (flowmeter) =>
              flowmeter.id === mapping.flowmeter_id &&
              (!flowmeter.society_id ||
                flowmeter.society_id === flow.society_id),
          )
        : null;

      if (!pumpMeta) {
        toast.error(`Pump metadata not found for ${mapping.pump_id}`);
        return;
      }
      if (!tankMeta) {
        toast.error(
          `Destination tank or UG tank metadata not found for ${mapping.destination_tank_id}`,
        );
        return;
      }
      if (!sourceMeta) {
        toast.error(
          `Source, tank, or UG tank metadata not found for ${mapping.source_id}`,
        );
        return;
      }
      if (mapping.flowmeter_id && !flowmeterMeta) {
        toast.error(`Flowmeter metadata not found for ${mapping.flowmeter_id}`);
        return;
      }

      // Every pump carries its own source and flowmeter, in spec order.
      config[`pump${index + 1}`] = {
        pump_id: mapping.pump_id,
        energymeter: buildPumpEnergyMeterConfig(pumpMeta),
        destination: buildATBPCDestinationConfig(tankMeta),
        source: buildATBPCSourceConfig(sourceMeta),
        flowmeter: flowmeterMeta
          ? buildATBPCFlowmeterConfig(flowmeterMeta)
          : // No flowmeter mapped: send the block disabled.
            { ...buildATBPCFlowmeterConfig({}), status: 0 },
      };
    }

    config.additional_sensors = buildAdditionalSensorConfigs(deviceId);

    console.log(config, "Sending config to borewell pump controller");
    publishConfigWithAck(
      config,
      "Borewell pump controller configured successfully",
    );
  };

  const sendLevelConfig = (deviceId) => {
    const config = {
      config_type: "d_config",
      device_id: deviceId,
      device_type: "v_controller",
    };

    // ✅ Step 1: filter flows for this level controller
    const matchedEntries = flows.flatMap((flow) =>
      getFlowLevelControllerMappings(flow)
        .filter((entry) => entry.level_controller_id === deviceId)
        .map((entry) => ({ flow, entry })),
    );

    if (matchedEntries.length === 0) {
      toast.error("This level controller is not added to any flows");
      return;
    }

    // ✅ Step 2: set common fields
    config.society_id = matchedEntries[0].flow.society_id;
    config.tower_id = matchedEntries[0].flow.tower_id;
    config.mid = buildConfigMessageId();

    // ✅ Step 3: build tank mappings
    const tankConfigMap = new Map();
    let missingTankId = "";

    matchedEntries.forEach(({ entry }) => {
      (entry.valve_tank_mappings || []).forEach((mapping) => {
        const tankId = cleanFlowRef(mapping.tank_id);
        if (!tankId) return;

        const valveId = cleanFlowRef(mapping.valve_id) || "-";
        const mapKey = `${tankId}::${valveId}`;
        const tankMeta = tanks.find((tank) => tank.tank_id === tankId);
        if (!tankMeta) {
          missingTankId = tankId;
          return;
        }

        if (!tankConfigMap.has(mapKey)) {
          tankConfigMap.set(mapKey, {
            sensor_type: getTankSensorTypeCode(tankMeta?.sensor_type),
            tank_id: tankId,
            associated_valve_id: valveId,
            associated_pump_ids: [],
            tank_height: getNumberOrDefault(tankMeta?.height, 0),
            sensor_slave_id: getNumberOrDefault(tankMeta?.sensor_slave_id, 1),
            bme_offset: getNumberOrDefault(tankMeta?.bme_offset, 0.0),
            publish: getBooleanOrDefault(tankMeta?.publish, true),
            cluster_count: getNumberOrDefault(tankMeta?.cluster_count, 0),
            cluster_slave_id_start: getNumberOrDefault(
              tankMeta?.cluster_slave_id_start,
              0,
            ),
          });
        }

        const tankConfig = tankConfigMap.get(mapKey);
        getMappingPumps(mapping).forEach(({ pump_id: pumpId }) => {
          if (!tankConfig.associated_pump_ids.includes(pumpId)) {
            tankConfig.associated_pump_ids.push(pumpId);
          }
        });
      });
    });

    if (missingTankId) {
      toast.error(`Tank metadata not found for ${missingTankId}`);
      return;
    }

    if (tankConfigMap.size === 0) {
      toast.error("No tanks are mapped to this level controller");
      return;
    }

    Array.from(tankConfigMap.values()).forEach((tankConfig, index) => {
      config[`tank${index + 1}`] = tankConfig;
    });

    config.network = getLevelControllerNetwork(deviceId);
    config.additional_sensors = buildAdditionalSensorConfigs(deviceId);

    console.log(config, "Sending config to level controller");
    publishConfigWithAck(config, "Level controller configured successfully");
  };

  // const sendLevelConfig = (deviceId) => {
  //   console.log("Sending level config to device:", deviceId);
  //   toast.success(`Config sent to level controller ${deviceId}`);
  // };

  useEffect(() => {
    fetchAllAssets();
  }, []);

  // MQTT SETUP
  useEffect(() => {
    const client = mqtt.connect(MQTT_BROKER_URL, {
      username: MQTT_USERNAME,
      password: MQTT_PASSWORD,
      clientId: `at_comp_${Math.random().toString(16).slice(2, 10)}`,
      clean: true,
      connectTimeout: 10000,
      reconnectPeriod: 3000,
    });

    clientRef.current = client;

    client.on("connect", () => {
      console.log("✅ MQTT Connected", MQTT_BROKER_URL);

      client.subscribe(MQTT_D2C_TOPIC, { qos: 1 }, (err) => {
        if (err) {
          console.error("❌ MQTT subscribe failed:", err);
          return;
        }
        console.log("Subscribed to", MQTT_D2C_TOPIC);
      });
    });

    client.on("message", (topic, message) => {
      let payload;
      try {
        payload = JSON.parse(message.toString());
      } catch (err) {
        console.error("Invalid MQTT message", topic, err);
        return;
      }

      console.log(payload, "Received message from device");
      const pendingAck = pendingConfigAckRef.current;
      const result = normalizeConfigResult(payload.set_config_result);
      if (
        pendingAck &&
        payload.device_id === pendingAck.device_id &&
        payload.device_type === pendingAck.device_type &&
        payload.config_type === pendingAck.config_type &&
        ["pass", "fail"].includes(result)
      ) {
        pendingAck.handleAck({ ...payload, set_config_result: result });
      }
    });

    client.on("error", (err) => {
      console.error("❌ MQTT Error:", err);
    });

    client.on("reconnect", () => {
      console.log("🔄 Reconnecting to MQTT broker...");
    });

    client.on("offline", () => {
      console.warn("MQTT client is offline");
    });

    client.on("close", () => {
      console.warn("MQTT connection closed");
    });

    client.on("end", () => {
      console.log("MQTT client ended");
    });

    return () => {
      if (clientRef.current === client) {
        clientRef.current = null;
      }
      const pendingAck = pendingConfigAckRef.current;
      if (pendingAck?.intervalId) clearInterval(pendingAck.intervalId);
      if (pendingAck?.timeoutId) clearTimeout(pendingAck.timeoutId);
      pendingConfigAckRef.current = null;
      toast.dismiss(CONFIG_ACK_TOAST_ID);
      client.end(true);
      console.log("🔌 MQTT client stopped");
    };
  }, []);

  // Delete handler
  const handleDelete = async (assetType, id) => {
    const typeLabels = {
      tower: "Tower",
      source: "Source",
      flowmeter: "Flowmeter",
      ug_tank: "UG Tank",
      atbc: "Pump Controller",
      atbpc: "Borewell Pump Ctrl",
      aquatank: "Level Controller",
      additional_sensor: "Additional Sensor",
      pump: "Pump",
      tank: "Tank",
      valve: "Valve",
    };

    if (
      !window.confirm(
        `Delete this ${typeLabels[assetType] || assetType} (ID: ${id})?`,
      )
    ) {
      return;
    }

    try {
      let result;
      switch (assetType) {
        case "tower":
          result = await api.deleteTower(id);
          break;
        case "source":
          result = await api.deleteSource(id);
          break;
        case "flowmeter":
          result = await api.deleteFlowmeter(id);
          break;
        case "ug_tank":
          result = await api.deleteUGTank(id);
          break;
        case "atbc":
          result = await api.deleteATBC(id);
          break;
        case "atbpc":
          result = await api.deleteBorewellPumpController(id);
          break;
        case "aquatank":
          result = await api.deleteAquaTank(id);
          break;
        case "additional_sensor":
          result = await api.deleteAdditionalSensor(id);
          break;
        case "pump":
          result = await api.deletePump(id);
          break;
        case "tank":
          result = await api.deleteTank(id);
          break;
        case "valve":
          result = await api.deleteValve(id);
          break;
        default:
          throw new Error("Unknown asset type");
      }

      toast.success(
        `${typeLabels[assetType] || assetType} deleted successfully`,
      );
      await fetchAllAssets(true); // Refresh
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(
        getApiErrorMessage(
          error,
          `Failed to delete ${typeLabels[assetType] || assetType}`,
        ),
      );
    }
  };

  const getNestedValue = (source, path) =>
    path.split(".").reduce((value, key) => value?.[key], source);

  const setNestedValue = (target, path, value) => {
    const keys = path.split(".");
    let cursor = target;

    keys.slice(0, -1).forEach((key) => {
      cursor[key] = cursor[key] || {};
      cursor = cursor[key];
    });

    cursor[keys[keys.length - 1]] = value;
  };

  const openEdit = (assetType, item) => {
    const config = editConfigs[assetType];
    const form = {};
    config.fields.forEach((field) => {
      const value = field.path
        ? getNestedValue(item, field.path)
        : item[field.key];
      const normalizedValue = field.normalize ? field.normalize(value) : value;
      form[field.key] =
        field.type === "select"
          ? normalizedValue === null || normalizedValue === undefined
            ? ""
            : String(normalizedValue)
          : (normalizedValue ?? (field.type === "checkbox" ? false : ""));
    });
    setEditingAsset({ assetType, item });
    setEditForm(form);
  };

  const closeEdit = () => {
    setEditingAsset(null);
    setEditForm({});
  };

  const handleEditSubmit = async () => {
    if (!editingAsset) return;

    const { assetType, item } = editingAsset;
    const config = editConfigs[assetType];
    const payload = {};

    if (assetType === "source") {
      const sourceId = String(editForm.source_id || "").trim();
      const sourceType = String(editForm.type || "").trim();

      if (!sourceId) {
        toast.error("Source ID is required");
        return;
      }
      if (!sourceType) {
        toast.error("Please select a source type");
        return;
      }

      if (!["0", "1"].includes(sourceType)) {
        toast.error("Source type must be borewell or tank");
        return;
      }

      const requiredNumberFields = editConfigs.source.fields.filter(
        (field) => field.type === "number" || field.valueType === "number",
      );

      for (const field of requiredNumberFields) {
        const value = editForm[field.key];
        if (value === "" || value === null || value === undefined) {
          toast.error(`${field.label} is required`);
          return;
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          toast.error(`${field.label} must be a number`);
          return;
        }

        if (field.integer && !Number.isInteger(numericValue)) {
          toast.error(`${field.label} must be an integer`);
          return;
        }

        if (field.min !== undefined && numericValue < field.min) {
          toast.error(`${field.label} must be at least ${field.min}`);
          return;
        }

        if (field.max !== undefined && numericValue > field.max) {
          toast.error(`${field.label} must be at most ${field.max}`);
          return;
        }
      }
    }

    if (assetType === "tank") {
      const requiredNumberFields = [
        { key: "bme_offset", label: "BME Offset" },
        {
          key: "cluster_count",
          label: "Cluster Count",
          min: 0,
          integer: true,
        },
        {
          key: "cluster_slave_id_start",
          label: "Cluster Slave ID Start",
          min: 0,
          max: 500,
          integer: true,
        },
      ];

      for (const field of requiredNumberFields) {
        const value = editForm[field.key];
        if (value === "" || value === null || value === undefined) {
          toast.error(`${field.label} is required`);
          return;
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          toast.error(`${field.label} must be a number`);
          return;
        }

        if (field.integer && !Number.isInteger(numericValue)) {
          toast.error(`${field.label} must be an integer`);
          return;
        }

        if (field.min !== undefined && numericValue < field.min) {
          toast.error(`${field.label} must be at least ${field.min}`);
          return;
        }

        if (field.max !== undefined && numericValue > field.max) {
          toast.error(`${field.label} must be at most ${field.max}`);
          return;
        }
      }
    }

    if (assetType === "ug_tank") {
      const requiredNumberFields = editConfigs.ug_tank.fields.filter(
        (field) => field.type === "number" || field.valueType === "number",
      );

      for (const field of requiredNumberFields) {
        const value = editForm[field.key];
        if (value === "" || value === null || value === undefined) {
          toast.error(`${field.label} is required`);
          return;
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          toast.error(`${field.label} must be a number`);
          return;
        }

        if (field.integer && !Number.isInteger(numericValue)) {
          toast.error(`${field.label} must be an integer`);
          return;
        }

        if (field.min !== undefined && numericValue < field.min) {
          toast.error(`${field.label} must be at least ${field.min}`);
          return;
        }

        if (field.max !== undefined && numericValue > field.max) {
          toast.error(`${field.label} must be at most ${field.max}`);
          return;
        }
      }
    }

    if (assetType === "pump") {
      const controlType = Number(editForm.control_type);
      if (![0, 1, 2, 3].includes(controlType)) {
        toast.error(
          "Control Type must be Direct, VFD, PressureRange or VFD_Direct",
        );
        return;
      }

      if (
        controlType === 2 &&
        String(editForm.pressureRangePtId || "").trim() === ""
      ) {
        toast.error("Pressure Range PT ID is required");
        return;
      }

      const requiredNumberFields = editConfigs.pump.fields.filter(
        (field) =>
          (field.type === "number" || field.valueType === "number") &&
          isEditFieldVisible(assetType, field),
      );

      for (const field of requiredNumberFields) {
        const value = editForm[field.key];
        if (value === "" || value === null || value === undefined) {
          toast.error(`${field.label} is required`);
          return;
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          toast.error(`${field.label} must be a number`);
          return;
        }

        if (field.integer && !Number.isInteger(numericValue)) {
          toast.error(`${field.label} must be an integer`);
          return;
        }

        if (field.min !== undefined && numericValue < field.min) {
          toast.error(`${field.label} must be at least ${field.min}`);
          return;
        }

        if (field.max !== undefined && numericValue > field.max) {
          toast.error(`${field.label} must be at most ${field.max}`);
          return;
        }
      }
    }

    if (assetType === "flowmeter") {
      const requiredNumberFields = editConfigs.flowmeter.fields.filter(
        (field) => field.type === "number" || field.valueType === "number",
      );

      for (const field of requiredNumberFields) {
        const value = editForm[field.key];
        if (value === "" || value === null || value === undefined) {
          toast.error(`${field.label} is required`);
          return;
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          toast.error(`${field.label} must be a number`);
          return;
        }

        if (field.integer && !Number.isInteger(numericValue)) {
          toast.error(`${field.label} must be an integer`);
          return;
        }

        if (field.min !== undefined && numericValue < field.min) {
          toast.error(`${field.label} must be at least ${field.min}`);
          return;
        }

        if (field.max !== undefined && numericValue > field.max) {
          toast.error(`${field.label} must be at most ${field.max}`);
          return;
        }
      }
    }

    if (assetType === "additional_sensor") {
      const sensorId = String(editForm.s_id || "").trim();
      const deviceId = String(editForm.device_id || "").trim();

      if (!sensorId) {
        toast.error("Sensor ID is required");
        return;
      }

      if (!deviceId) {
        toast.error("Device ID is required");
        return;
      }

      const requiredNumberFields = editConfigs.additional_sensor.fields.filter(
        (field) => field.type === "number" || field.valueType === "number",
      );

      for (const field of requiredNumberFields) {
        const value = editForm[field.key];
        if (value === "" || value === null || value === undefined) {
          toast.error(`${field.label} is required`);
          return;
        }

        const numericValue = Number(value);
        if (!Number.isFinite(numericValue)) {
          toast.error(`${field.label} must be a number`);
          return;
        }

        if (field.integer && !Number.isInteger(numericValue)) {
          toast.error(`${field.label} must be an integer`);
          return;
        }

        if (field.min !== undefined && numericValue < field.min) {
          toast.error(`${field.label} must be at least ${field.min}`);
          return;
        }

        if (field.max !== undefined && numericValue > field.max) {
          toast.error(`${field.label} must be at most ${field.max}`);
          return;
        }
      }
    }

    config.fields
      .filter((field) => isEditFieldVisible(assetType, field))
      .forEach((field) => {
        const value = editForm[field.key];
        if (field.type === "number" || field.valueType === "number") {
          const nextValue =
            value === "" || value === null ? null : Number(value);
          if (field.path) {
            setNestedValue(payload, field.path, nextValue);
          } else {
            payload[field.key] = nextValue;
          }
        } else {
          if (field.path) {
            setNestedValue(payload, field.path, value);
          } else {
            payload[field.key] = value;
          }
        }
      });

    setSavingEdit(true);
    try {
      await config.update(getAssetId(item, assetType), payload);
      toast.success(`${config.label} updated successfully`);
      closeEdit();
      await fetchAllAssets(true);
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(
        error.response?.data?.detail || `Failed to update ${config.label}`,
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const pumpBaseColumns = [
    { key: "pump_id", label: "Pump ID" },
    { key: "name", label: "Name" },
    {
      key: "power_rating",
      label: "Power Rating",
      render: (val) => formatDisplayUnit(val, "W"),
    },
    { key: "model", label: "Model" },
    {
      key: "energyMeterStatus",
      label: "EM Status",
      render: (val, item) =>
        getOptionLabel(CONNECTED_STATUS_OPTIONS, item.energymeter?.status),
    },
    {
      key: "energyMeterMake",
      label: "EM Make",
      render: (val, item) =>
        getOptionLabel(ENERGY_METER_MAKE_OPTIONS, item.energymeter?.make),
    },
    {
      key: "energyMeterSlaveId",
      label: "EM Slave",
      render: (val, item) => item.energymeter?.slave_id ?? "-",
    },
    {
      key: "control_type",
      label: "Control Type",
      render: (val) => getOptionLabel(PUMP_CONTROL_TYPE_OPTIONS, val),
    },
  ];

  const pumpVfdColumns = [
    {
      key: "vfdSlaveId",
      label: "VFD Slave",
      render: (val, item) => item.vfd?.slave_id ?? "-",
    },
    {
      key: "vfdPressureSlaveId",
      label: "Pressure Slave",
      render: (val, item) => item.vfd?.pressure_slave_id ?? "-",
    },
    {
      key: "pressureStartAddress",
      label: "Pressure Addr",
      render: (val, item) => item.vfd?.pressure_start_address ?? "-",
    },
    {
      key: "pressureQuantity",
      label: "Pressure Qty",
      render: (val, item) => item.vfd?.pressure_quantity ?? "-",
    },
    {
      key: "pressureDataType",
      label: "Pressure Type",
      render: (val, item) =>
        getOptionLabel(MODBUS_DATA_TYPE_OPTIONS, item.vfd?.pressure_data_type),
    },
    {
      key: "pressureOrder",
      label: "Pressure Order",
      render: (val, item) =>
        getOptionLabel(MODBUS_ORDER_OPTIONS, item.vfd?.pressure_order),
    },
    {
      key: "pressureScale",
      label: "Pressure Scale",
      render: (val, item) => item.vfd?.pressure_scale ?? "-",
    },
    {
      key: "pressureOffset",
      label: "Pressure Offset",
      render: (val, item) => item.vfd?.pressure_offset ?? "-",
    },
    {
      key: "rangeLowBar",
      label: "Low Bar",
      render: (val, item) => item.vfd?.range_low_bar ?? "-",
    },
    {
      key: "rangeHighBar",
      label: "High Bar",
      render: (val, item) => item.vfd?.range_high_bar ?? "-",
    },
    {
      key: "minFreqHz",
      label: "Min Hz",
      render: (val, item) => item.vfd?.min_freq_hz ?? "-",
    },
    {
      key: "maxFreqHz",
      label: "Max Hz",
      render: (val, item) => item.vfd?.max_freq_hz ?? "-",
    },
  ];

  const pumpPressureRangeColumns = [
    {
      key: "pressureRangeIsSource",
      label: "PR Source",
      render: (val, item) => (item.pressure_range?.is_source ? "Yes" : "No"),
    },
    {
      key: "pressureRangePtId",
      label: "PR PT ID",
      render: (val, item) => item.pressure_range?.pt_id ?? "-",
    },
    {
      key: "pressureRangeStageIndex",
      label: "PR Stage",
      render: (val, item) => item.pressure_range?.stage_index ?? "-",
    },
    {
      key: "pressureRangePtSlaveId",
      label: "PR Slave",
      render: (val, item) => item.pressure_range?.pt_slave_id ?? "-",
    },
    {
      key: "pressureRangePtStartAddress",
      label: "PR Addr",
      render: (val, item) => item.pressure_range?.pt_start_address ?? "-",
    },
    {
      key: "pressureRangePtQuantity",
      label: "PR Qty",
      render: (val, item) => item.pressure_range?.pt_quantity ?? "-",
    },
    {
      key: "pressureRangePtDataType",
      label: "PR Type",
      render: (val, item) => item.pressure_range?.pt_data_type ?? "-",
    },
    {
      key: "pressureRangePtOrder",
      label: "PR Order",
      render: (val, item) => item.pressure_range?.pt_order ?? "-",
    },
    {
      key: "pressureRangePtScale",
      label: "PR Scale",
      render: (val, item) => item.pressure_range?.pt_scale ?? "-",
    },
    {
      key: "pressureRangePtOffset",
      label: "PR Offset",
      render: (val, item) => item.pressure_range?.pt_offset ?? "-",
    },
    {
      key: "pressureRangeLowBar",
      label: "PR Low Bar",
      render: (val, item) => item.pressure_range?.range_low_bar ?? "-",
    },
    {
      key: "pressureRangeHighBar",
      label: "PR High Bar",
      render: (val, item) => item.pressure_range?.range_high_bar ?? "-",
    },
  ];

  const pumpTailColumns = [
    {
      key: "control",
      label: "Control",
      render: (val) =>
        val ? (
          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
            Yes
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-800">
            No
          </span>
        ),
    },
    {
      key: "created_at",
      label: "Created",
      render: (val) => (val ? new Date(val).toLocaleDateString() : "-"),
    },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-screen">
          <Loader className="h-12 w-12 text-blue-500 animate-spin" />
          <p className="mt-4 text-slate-600">Loading assets...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">Assets</h1>
            <p className="text-slate-600 mt-1">
              View and manage all system assets and devices
            </p>
          </div>
          <Button
            onClick={() => fetchAllAssets(true)}
            disabled={refreshing}
            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-700"
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>

        {editingAsset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Edit {editConfigs[editingAsset.assetType].label}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Changes are applied to existing architecture references when
                    IDs change.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeEdit}
                  className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="max-h-[65vh] space-y-4 overflow-y-auto px-5 py-4">
                {editConfigs[editingAsset.assetType].fields
                  .filter((field) =>
                    isEditFieldVisible(editingAsset.assetType, field),
                  )
                  .map((field) => (
                    <div key={field.key}>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        {field.label}
                      </label>
                      {field.type === "checkbox" ? (
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(editForm[field.key])}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                [field.key]: e.target.checked,
                              }))
                            }
                            className="h-4 w-4"
                          />
                          Enabled
                        </label>
                      ) : field.type === "select" ? (
                        <select
                          value={editForm[field.key] ?? ""}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-slate-900 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <option value="" disabled>
                            Select {field.label}
                          </option>
                          {field.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          type={field.type || "text"}
                          min={field.min}
                          max={field.max}
                          value={editForm[field.key] ?? ""}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                        />
                      )}
                    </div>
                  ))}
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
                <Button
                  variant="outline"
                  onClick={closeEdit}
                  disabled={savingEdit}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleEditSubmit}
                  disabled={savingEdit}
                  className="bg-slate-900 hover:bg-slate-700"
                >
                  {savingEdit ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="towers" className="w-full">
          <TabsList className="grid w-full grid-cols-11 bg-slate-100 p-1 h-auto">
            <TabsTrigger
              value="towers"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Towers</span>
              <span className="sm:hidden text-xs">{towers.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="sources"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Droplets className="h-4 w-4" />
              <span className="hidden sm:inline">Sources</span>
              <span className="sm:hidden text-xs">{sources.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="flowmeter"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Flowmeter</span>
              <span className="sm:hidden text-xs">{flowmeters.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="ug-tanks"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Droplets className="h-4 w-4" />
              <span className="hidden sm:inline">UG Tanks</span>
              <span className="sm:hidden text-xs">{ugTanks.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="pump-controllers"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">Pump Ctrl</span>
              <span className="sm:hidden text-xs">
                {pumpControllers.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="borewell-pump-ctrl"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">B Pump Ctrl</span>
              <span className="sm:hidden text-xs">
                {borewellPumpControllers.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="level-controllers"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Network className="h-4 w-4" />
              <span className="hidden sm:inline">Level Ctrl</span>
              <span className="sm:hidden text-xs">
                {levelControllers.length}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="pumps"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Pumps</span>
              <span className="sm:hidden text-xs">{pumps.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="tanks"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Waves className="h-4 w-4" />
              <span className="hidden sm:inline">Tanks</span>
              <span className="sm:hidden text-xs">{tanks.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="valves"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Network className="h-4 w-4" />
              <span className="hidden sm:inline">Valves</span>
              <span className="sm:hidden text-xs">{valves.length}</span>
            </TabsTrigger>
            <TabsTrigger
              value="additional-sensors"
              className="flex items-center gap-2 data-[state=active]:bg-white"
            >
              <Activity className="h-4 w-4" />
              <span className="hidden sm:inline">Sensors</span>
              <span className="sm:hidden text-xs">
                {additionalSensors.length}
              </span>
            </TabsTrigger>
          </TabsList>

          {/* Towers Tab */}
          <TabsContent value="towers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Towers</CardTitle>
                <CardDescription>
                  All towers registered under the current super-admin society
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Towers"
                  icon={Building2}
                  data={towers}
                  searchTerm={searchTowers}
                  setSearchTerm={setSearchTowers}
                  columns={[
                    { key: "name", label: "Name" },
                    { key: "location", label: "Location" },
                    {
                      key: "active",
                      label: "Status",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Active
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            Inactive
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-blue-500 to-blue-600"
                  emptyMessage="No towers found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sources Tab */}
          <TabsContent value="sources" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Sources</CardTitle>
                <CardDescription>
                  Society source configured for water intake
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Sources"
                  icon={Droplets}
                  data={sources}
                  searchTerm={searchSources}
                  setSearchTerm={setSearchSources}
                  columns={[
                    { key: "source_id", label: "Source ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "type",
                      label: "Type",
                      render: (val) => (
                        <span className="px-2 py-1 bg-cyan-100 text-cyan-800 rounded-full text-xs font-medium">
                          {Number(val) === 0
                            ? "Borewell"
                            : Number(val) === 1
                              ? "Tank"
                              : "-"}
                        </span>
                      ),
                    },
                    {
                      key: "status",
                      label: "Status",
                      render: (val) =>
                        getOptionLabel(SOURCE_STATUS_OPTIONS, val),
                    },
                    {
                      key: "sensor_type",
                      label: "Sensor Type",
                      render: (val) =>
                        getOptionLabel(SOURCE_SENSOR_TYPE_OPTIONS, val),
                    },
                    { key: "sensor_slave_id", label: "Sensor Slave ID" },
                    { key: "cable_length_m", label: "Cable Length (m)" },
                    { key: "tank_height", label: "Tank Height" },
                    { key: "low", label: "Low" },
                    { key: "med", label: "Mid" },
                    { key: "high", label: "High" },
                    {
                      key: "publish",
                      label: "Publish",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    { key: "location", label: "Location" },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-cyan-500 to-cyan-600"
                  emptyMessage="No source found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Flowmeter Tab */}
          <TabsContent value="flowmeter" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Flowmeter</CardTitle>
                <CardDescription>
                  Flowmeter register configuration for this society
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FlowmeterAssetSection
                  data={flowmeters}
                  searchTerm={searchFlowmeters}
                  setSearchTerm={setSearchFlowmeters}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* UG Tanks Tab */}
          <TabsContent value="ug-tanks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Underground Tanks</CardTitle>
                <CardDescription>
                  All registered underground water tanks in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="UG Tanks"
                  icon={Droplets}
                  data={ugTanks}
                  searchTerm={searchUg}
                  setSearchTerm={setSearchUg}
                  columns={[
                    { key: "ug_tank_id", label: "Tank ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "capacity",
                      label: "Capacity",
                      render: (val) => formatDisplayUnit(val, "L"),
                    },
                    {
                      key: "height",
                      label: "Height",
                      render: (val) => formatDisplayUnit(val, "m"),
                    },
                    { key: "low", label: "Low" },
                    { key: "med", label: "Mid" },
                    { key: "high", label: "High" },
                    {
                      key: "sensor_type",
                      label: "Sensor Type",
                      render: getTankSensorTypeLabel,
                    },
                    { key: "sensor_slave_id", label: "Sensor Slave ID" },
                    { key: "bme_offset", label: "BME Offset" },
                    {
                      key: "publish",
                      label: "Publish",
                      render: (val) =>
                        val ? (
                          <span className="text-emerald-600 font-semibold">
                            Yes
                          </span>
                        ) : (
                          <span className="text-slate-500 font-semibold">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "control",
                      label: "Control",
                      render: (val) =>
                        val ? (
                          <span className="text-emerald-600 font-semibold">
                            Yes
                          </span>
                        ) : (
                          <span className="text-slate-500 font-semibold">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-cyan-500 to-cyan-600"
                  emptyMessage="No underground tanks found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pump Controllers Tab */}
          <TabsContent value="pump-controllers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pump Controllers</CardTitle>
                <CardDescription>
                  All pump control devices in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Pump Controllers"
                  icon={Zap}
                  data={pumpControllers}
                  searchTerm={searchPumpCtrl}
                  setSearchTerm={setSearchPumpCtrl}
                  columns={[
                    { key: "device_id", label: "Device ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "connectionType",
                      label: "Connection",
                      render: (val, item) =>
                        getLevelConnectionTypeLabel(
                          item.network?.connectionType,
                        ),
                    },
                    {
                      key: "gsmISP",
                      label: "GSM ISP",
                      render: (val, item) =>
                        getGsmApnLabel(item.network?.gsmISP),
                    },
                    {
                      key: "wifiUsn",
                      label: "WiFi User",
                      render: (val, item) => item.network?.wifiUsn || "-",
                    },
                    {
                      key: "device_conf",
                      label: "Configure Device",
                      render: (val, item) => (
                        <Button
                          onClick={() => sendPumpConfig(item.device_id)}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                          disabled={Boolean(configuringDeviceId)}
                        >
                          {configuringDeviceId === item.device_id
                            ? "Waiting..."
                            : item.isConfigured
                              ? "Reconfigure"
                              : "Configure"}
                        </Button>
                      ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-yellow-500 to-yellow-600"
                  emptyMessage="No pump controllers found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Borewell Pump Controllers Tab */}
          <TabsContent value="borewell-pump-ctrl" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Borewell Pump Ctrl</CardTitle>
                <CardDescription>
                  All borewell pump controller devices in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Borewell Pump Ctrl"
                  icon={Zap}
                  data={borewellPumpControllers}
                  searchTerm={searchBorewellPumpCtrl}
                  setSearchTerm={setSearchBorewellPumpCtrl}
                  columns={[
                    { key: "device_id", label: "Device ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "connectionType",
                      label: "Connection",
                      render: (val, item) =>
                        getLevelConnectionTypeLabel(
                          item.network?.connectionType,
                        ),
                    },
                    {
                      key: "gsmISP",
                      label: "GSM ISP",
                      render: (val, item) =>
                        getGsmApnLabel(item.network?.gsmISP),
                    },
                    {
                      key: "wifiUsn",
                      label: "WiFi User",
                      render: (val, item) => item.network?.wifiUsn || "-",
                    },
                    {
                      key: "device_conf",
                      label: "Configure Device",
                      render: (val, item) => (
                        <Button
                          onClick={() => sendATBPCConfig(item.device_id)}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                          disabled={Boolean(configuringDeviceId)}
                        >
                          {configuringDeviceId === item.device_id
                            ? "Waiting..."
                            : item.isConfigured
                              ? "Reconfigure"
                              : "Configure"}
                        </Button>
                      ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-emerald-500 to-emerald-600"
                  emptyMessage="No borewell pump controllers found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Level Controllers Tab */}
          <TabsContent value="level-controllers" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Level Controllers</CardTitle>
                <CardDescription>
                  All water level monitoring devices in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Level Controllers"
                  icon={Network}
                  data={levelControllers}
                  searchTerm={searchLevelCtrl}
                  setSearchTerm={setSearchLevelCtrl}
                  columns={[
                    { key: "device_id", label: "Device ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "connectionType",
                      label: "Connection",
                      render: (val, item) =>
                        getLevelConnectionTypeLabel(
                          item.network?.connectionType,
                        ),
                    },
                    {
                      key: "device_conf",
                      label: "Device Conf",
                      render: (val, item) => (
                        <Button
                          onClick={() => sendLevelConfig(item.device_id)}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3 text-xs"
                          disabled={Boolean(configuringDeviceId)}
                        >
                          {configuringDeviceId === item.device_id
                            ? "Waiting..."
                            : item.isConfigured
                              ? "Reconfigure"
                              : "Configure"}
                        </Button>
                      ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-indigo-500 to-indigo-600"
                  emptyMessage="No level controllers found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pumps Tab */}
          <TabsContent value="pumps" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Pumps</CardTitle>
                <CardDescription>
                  All water pumps registered in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Pumps"
                  icon={Activity}
                  data={pumps}
                  searchTerm={searchPumps}
                  setSearchTerm={setSearchPumps}
                  columns={[
                    { key: "pump_id", label: "Pump ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "power_rating",
                      label: "Power Rating",
                      render: (val) => formatDisplayUnit(val, "W"),
                    },
                    { key: "model", label: "Model" },
                    {
                      key: "energyMeterStatus",
                      label: "EM Status",
                      render: (val, item) =>
                        getOptionLabel(
                          CONNECTED_STATUS_OPTIONS,
                          item.energymeter?.status,
                        ),
                    },
                    {
                      key: "energyMeterMake",
                      label: "EM Make",
                      render: (val, item) =>
                        getOptionLabel(
                          ENERGY_METER_MAKE_OPTIONS,
                          item.energymeter?.make,
                        ),
                    },
                    {
                      key: "energyMeterSlaveId",
                      label: "EM Slave",
                      render: (val, item) => item.energymeter?.slave_id ?? "-",
                    },
                    {
                      key: "control_type",
                      label: "Control Type",
                      render: (val) =>
                        getOptionLabel(PUMP_CONTROL_TYPE_OPTIONS, val),
                    },
                    {
                      key: "vfdSlaveId",
                      label: "VFD Slave",
                      render: (val, item) => item.vfd?.slave_id ?? "-",
                    },
                    {
                      key: "vfdPressureSlaveId",
                      label: "Pressure Slave",
                      render: (val, item) => item.vfd?.pressure_slave_id ?? "-",
                    },
                    {
                      key: "pressureStartAddress",
                      label: "Pressure Addr",
                      render: (val, item) =>
                        item.vfd?.pressure_start_address ?? "-",
                    },
                    {
                      key: "pressureQuantity",
                      label: "Pressure Qty",
                      render: (val, item) => item.vfd?.pressure_quantity ?? "-",
                    },
                    {
                      key: "pressureDataType",
                      label: "Pressure Type",
                      render: (val, item) =>
                        getOptionLabel(
                          MODBUS_DATA_TYPE_OPTIONS,
                          item.vfd?.pressure_data_type,
                        ),
                    },
                    {
                      key: "pressureOrder",
                      label: "Pressure Order",
                      render: (val, item) =>
                        getOptionLabel(
                          MODBUS_ORDER_OPTIONS,
                          item.vfd?.pressure_order,
                        ),
                    },
                    {
                      key: "pressureScale",
                      label: "Pressure Scale",
                      render: (val, item) => item.vfd?.pressure_scale ?? "-",
                    },
                    {
                      key: "pressureOffset",
                      label: "Pressure Offset",
                      render: (val, item) => item.vfd?.pressure_offset ?? "-",
                    },
                    {
                      key: "rangeLowBar",
                      label: "Low Bar",
                      render: (val, item) => item.vfd?.range_low_bar ?? "-",
                    },
                    {
                      key: "rangeHighBar",
                      label: "High Bar",
                      render: (val, item) => item.vfd?.range_high_bar ?? "-",
                    },
                    {
                      key: "minFreqHz",
                      label: "Min Hz",
                      render: (val, item) => item.vfd?.min_freq_hz ?? "-",
                    },
                    {
                      key: "maxFreqHz",
                      label: "Max Hz",
                      render: (val, item) => item.vfd?.max_freq_hz ?? "-",
                    },
                    {
                      key: "pressureRangeIsSource",
                      label: "PR Source",
                      render: (val, item) =>
                        item.pressure_range?.is_source ? "Yes" : "No",
                    },
                    {
                      key: "pressureRangePtId",
                      label: "PR PT ID",
                      render: (val, item) => item.pressure_range?.pt_id ?? "-",
                    },
                    {
                      key: "pressureRangeStageIndex",
                      label: "PR Stage",
                      render: (val, item) =>
                        item.pressure_range?.stage_index ?? "-",
                    },
                    {
                      key: "pressureRangePtSlaveId",
                      label: "PR Slave",
                      render: (val, item) =>
                        item.pressure_range?.pt_slave_id ?? "-",
                    },
                    {
                      key: "pressureRangePtStartAddress",
                      label: "PR Addr",
                      render: (val, item) =>
                        item.pressure_range?.pt_start_address ?? "-",
                    },
                    {
                      key: "pressureRangePtQuantity",
                      label: "PR Qty",
                      render: (val, item) =>
                        item.pressure_range?.pt_quantity ?? "-",
                    },
                    {
                      key: "pressureRangePtDataType",
                      label: "PR Type",
                      render: (val, item) =>
                        item.pressure_range?.pt_data_type ?? "-",
                    },
                    {
                      key: "pressureRangePtOrder",
                      label: "PR Order",
                      render: (val, item) =>
                        item.pressure_range?.pt_order ?? "-",
                    },
                    {
                      key: "pressureRangePtScale",
                      label: "PR Scale",
                      render: (val, item) =>
                        item.pressure_range?.pt_scale ?? "-",
                    },
                    {
                      key: "pressureRangePtOffset",
                      label: "PR Offset",
                      render: (val, item) =>
                        item.pressure_range?.pt_offset ?? "-",
                    },
                    {
                      key: "pressureRangeLowBar",
                      label: "PR Low Bar",
                      render: (val, item) =>
                        item.pressure_range?.range_low_bar ?? "-",
                    },
                    {
                      key: "pressureRangeHighBar",
                      label: "PR High Bar",
                      render: (val, item) =>
                        item.pressure_range?.range_high_bar ?? "-",
                    },
                    {
                      key: "control",
                      label: "Control",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-orange-500 to-orange-600"
                  emptyMessage="No pumps found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tanks Tab */}
          <TabsContent value="tanks" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Overhead Tanks</CardTitle>
                <CardDescription>
                  All overhead water storage tanks in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Tanks"
                  icon={Waves}
                  data={tanks}
                  searchTerm={searchTanks}
                  setSearchTerm={setSearchTanks}
                  columns={[
                    { key: "tank_id", label: "Tank ID" },
                    { key: "name", label: "Name" },
                    {
                      key: "capacity",
                      label: "Capacity",
                      render: (val) => formatDisplayUnit(val, "L"),
                    },
                    {
                      key: "sensor_type",
                      label: "Sensor Type",
                      render: getTankSensorTypeLabel,
                    },
                    { key: "sensor_slave_id", label: "Sensor Slave ID" },
                    { key: "low", label: "Low" },
                    { key: "med", label: "Med" },
                    { key: "high", label: "High" },
                    {
                      key: "height",
                      label: "Height",
                      render: (val) => formatDisplayUnit(val, "m"),
                    },
                    { key: "bme_offset", label: "BME Offset" },
                    {
                      key: "publish",
                      label: "Publish",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    { key: "cluster_count", label: "Cluster Count" },
                    {
                      key: "cluster_slave_id_start",
                      label: "Cluster Start",
                    },
                    {
                      key: "control",
                      label: "Control",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-teal-500 to-teal-600"
                  emptyMessage="No tanks found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Valves Tab */}
          <TabsContent value="valves" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Valves</CardTitle>
                <CardDescription>
                  All water control valves in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Valves"
                  icon={Network}
                  data={valves}
                  searchTerm={searchValves}
                  setSearchTerm={setSearchValves}
                  columns={[
                    { key: "valve_id", label: "Valve ID" },
                    {
                      key: "type",
                      label: "Type",
                      render: (val) => (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          {val || "Unknown"}
                        </span>
                      ),
                    },
                    {
                      key: "control",
                      label: "Control",
                      render: (val) =>
                        val ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-slate-100 text-slate-800 rounded-full text-xs font-medium">
                            No
                          </span>
                        ),
                    },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-pink-500 to-pink-600"
                  emptyMessage="No valves found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Additional Sensors Tab */}
          <TabsContent value="additional-sensors" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Additional Sensors</CardTitle>
                <CardDescription>
                  All additional sensor configurations mapped to controller
                  devices
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AssetSection
                  title="Additional Sensors"
                  icon={Activity}
                  data={additionalSensors}
                  searchTerm={searchAdditionalSensors}
                  setSearchTerm={setSearchAdditionalSensors}
                  columns={[
                    { key: "s_id", label: "Sensor ID" },
                    { key: "sensor_name", label: "Sensor Name" },
                    {
                      key: "unit",
                      label: "Unit",
                      render: (val) => val || "-",
                    },
                    { key: "device_id", label: "Device ID" },
                    { key: "slave_id", label: "Slave ID" },
                    { key: "start_address", label: "Start Address" },
                    { key: "quantity", label: "Quantity" },
                    {
                      key: "data_type",
                      label: "Data Type",
                      render: (val) =>
                        getOptionLabel(MODBUS_DATA_TYPE_OPTIONS, val),
                    },
                    {
                      key: "order",
                      label: "Order",
                      render: (val) =>
                        getOptionLabel(MODBUS_ORDER_OPTIONS, val),
                    },
                    { key: "scale", label: "Scale" },
                    { key: "offset", label: "Offset" },
                    {
                      key: "created_at",
                      label: "Created",
                      render: (val) =>
                        val ? new Date(val).toLocaleDateString() : "-",
                    },
                  ]}
                  bgColor="bg-gradient-to-br from-slate-600 to-slate-700"
                  emptyMessage="No additional sensors found"
                  onEdit={openEdit}
                  onDelete={handleDelete}
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-10 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-700">
                  {towers.length}
                </p>
                <p className="text-xs text-blue-600 mt-1">Towers</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-cyan-700">
                  {sources.length}
                </p>
                <p className="text-xs text-cyan-600 mt-1">Sources</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-cyan-700">
                  {ugTanks.length}
                </p>
                <p className="text-xs text-cyan-600 mt-1">UG Tanks</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-yellow-700">
                  {pumpControllers.length}
                </p>
                <p className="text-xs text-yellow-600 mt-1">Pump Ctrl</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-emerald-700">
                  {borewellPumpControllers.length}
                </p>
                <p className="text-xs text-emerald-600 mt-1">Borewell Ctrl</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-700">
                  {levelControllers.length}
                </p>
                <p className="text-xs text-indigo-600 mt-1">Level Ctrl</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-orange-700">
                  {pumps.length}
                </p>
                <p className="text-xs text-orange-600 mt-1">Pumps</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-700">
                  {tanks.length}
                </p>
                <p className="text-xs text-teal-600 mt-1">Tanks</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-pink-50 to-pink-100 border-pink-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-pink-700">
                  {valves.length}
                </p>
                <p className="text-xs text-pink-600 mt-1">Valves</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
            <CardContent className="pt-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-slate-700">
                  {additionalSensors.length}
                </p>
                <p className="text-xs text-slate-600 mt-1">Addl Sensors</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Components;
